import { useState, useEffect, useCallback } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown, Pin, Ban } from "lucide-react";
import * as api from "../../lib/api";

export const PRIMARY = "#018D4C";

export const INTENT_COLORS = {
  informational: { bg: "#EFF6FF", text: "#1D4ED8" },
  commercial:    { bg: "#FFFBEB", text: "#B45309" },
  transactional: { bg: "#ECFDF5", text: "#065F46" },
  comparison:    { bg: "#F5F3FF", text: "#6D28D9" },
  problem_aware: { bg: "#FFF1F2", text: "#BE123C" },
};

export const FIT_COLORS = {
  good_fit:        { bg: "#ECFDF5", text: "#065F46" },
  weak_fit:        { bg: "#FFFBEB", text: "#B45309" },
  wrong_page:      { bg: "#FFF1F2", text: "#BE123C" },
  missing_page:    { bg: "#F4F4F5", text: "#52525B" },
  cannibalization: { bg: "#F5F3FF", text: "#6D28D9" },
};

export const OPPORTUNITY_COLORS = {
  quick_win:          { bg: "#ECFDF5", text: "#065F46" },
  growth_opportunity: { bg: "#FFFBEB", text: "#B45309" },
  deep_opportunity:   { bg: "#FFF1F2", text: "#BE123C" },
  missing_coverage:   { bg: "#EFF6FF", text: "#1D4ED8" },
};

export function fmt(n, decimals = 0) {
  if (n == null) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: decimals });
}

export function pct(n) {
  if (n == null) return "—";
  return (Number(n) * 100).toFixed(1) + "%";
}

export function SortIcon({ col, sort, order }) {
  if (sort !== col) return <ChevronsUpDown size={11} className="text-zinc-300 ml-0.5" />;
  return order === "asc"
    ? <ArrowUp size={11} className="ml-0.5" style={{ color: PRIMARY }} />
    : <ArrowDown size={11} className="ml-0.5" style={{ color: PRIMARY }} />;
}

export function Th({ col, label, sort, order, onSort, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400 cursor-pointer select-none whitespace-nowrap ${className}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        <SortIcon col={col} sort={sort} order={order} />
      </span>
    </th>
  );
}

// One bordered strip of metrics instead of a card per number — keeps the header
// area light so the tables below get the visual weight.
function MetricStrip({ items, loading, muted = false }) {
  return (
    <div
      className={`grid grid-cols-2 sm:flex sm:divide-x divide-zinc-100 rounded-xl border border-zinc-100 ${
        muted ? "bg-zinc-50/60" : "bg-white shadow-sm"
      }`}
    >
      {items.map(({ label, value }) => (
        <div key={label} className="flex-1 min-w-0 px-4 py-2.5">
          <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-zinc-400 truncate">{label}</p>
          <p className={`mt-0.5 font-semibold tabular-nums text-zinc-900 ${muted ? "text-sm" : "text-lg"}`}>
            {loading ? "—" : value}
          </p>
        </div>
      ))}
    </div>
  );
}

export function StatBar({ totals, loading, daysBack = 28 }) {
  return (
    <MetricStrip
      loading={loading}
      items={[
        { label: `Clicks (${daysBack}d)`, value: fmt(totals?.clicks) },
        { label: `Impressions (${daysBack}d)`, value: fmt(totals?.impressions) },
        { label: "Avg CTR", value: pct(totals?.ctr) },
        { label: "Avg Position", value: fmt(totals?.position, 1) },
      ]}
    />
  );
}

export function OpportunitySummaryBar({ summary, loading }) {
  const opt = summary?.by_opportunity || {};
  const fit = summary?.by_page_fit || {};
  const optimize = (opt.quick_win || 0) + (opt.growth_opportunity || 0) + (opt.deep_opportunity || 0);
  return (
    <MetricStrip
      muted
      loading={loading}
      items={[
        { label: "Total Queries", value: fmt(summary?.total_queries) },
        { label: "Actionable", value: fmt(summary?.actionable) },
        { label: "New Pages", value: fmt(opt.missing_coverage) },
        { label: "Optimize", value: fmt(optimize) },
        { label: "Cannibalization", value: fmt(fit.cannibalization) },
      ]}
    />
  );
}

// Pinned queries/pages (gsc_pins). Keys are lowercased so "Buy CRM" and "buy crm" match.
const pinKey = (v) => (v || "").trim().toLowerCase().replace(/\/+$/, "");

export function usePins(kind) {
  const [pins, setPins] = useState(new Set());

  useEffect(() => {
    api.getGscPins({ kind })
      .then(r => setPins(new Set((r.pins || []).map(p => pinKey(p.value)))))
      .catch(() => {});
  }, [kind]);

  const toggle = useCallback(async (value) => {
    const key = pinKey(value);
    const wasPinned = pins.has(key);
    setPins(s => { const n = new Set(s); wasPinned ? n.delete(key) : n.add(key); return n; });
    try {
      if (wasPinned) await api.removeGscPin(kind, value);
      else await api.addGscPin(kind, value);
    } catch {
      setPins(s => { const n = new Set(s); wasPinned ? n.add(key) : n.delete(key); return n; });
    }
  }, [kind, pins]);

  return { isPinned: (v) => pins.has(pinKey(v)), toggle, count: pins.size };
}

export function PinButton({ pinned, onClick }) {
  return (
    <button
      onClick={onClick}
      title={pinned ? "Unpin" : "Pin to monitor in Trends"}
      className={`p-1 rounded transition-colors ${pinned ? "text-amber-500" : "text-zinc-300 hover:text-zinc-500"}`}
    >
      <Pin size={13} fill={pinned ? "currentColor" : "none"} />
    </button>
  );
}

// compact = icon-only toggle (count as a badge, label in the tooltip) for crowded toolbars.
function CompactFilterToggle({ active, count, onClick, label, color, children }) {
  return (
    <button
      onClick={onClick}
      title={`${active ? "Showing only" : "Show only"} ${label.toLowerCase()}${count ? ` (${count})` : ""}`}
      aria-pressed={active}
      className="relative p-2 rounded-lg transition-colors border"
      style={active
        ? { backgroundColor: color, color: "#fff", borderColor: color }
        : { background: "#fff", color: "#71717a", borderColor: "#f4f4f5" }}
    >
      {children}
      {count > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold leading-4 text-center"
          style={active ? { background: "#fff", color } : { background: "#e4e4e7", color: "#52525b" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function PinnedFilterButton({ active, count, onClick, compact = false }) {
  if (compact) {
    return (
      <CompactFilterToggle active={active} count={count} onClick={onClick} label="Pinned" color="#F59E0B">
        <Pin size={13} fill={active ? "currentColor" : "none"} />
      </CompactFilterToggle>
    );
  }
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold rounded-lg transition-colors border"
      style={active
        ? { backgroundColor: "#F59E0B", color: "#fff", borderColor: "#F59E0B" }
        : { background: "#fff", color: "#71717a", borderColor: "#f4f4f5" }}
    >
      <Pin size={10} fill={active ? "currentColor" : "none"} /> Pinned{count ? ` (${count})` : ""}
    </button>
  );
}

// kind='exclude' pins: queries Query Gap Analysis skips when classifying intent.
export function ExcludeButton({ excluded, onClick }) {
  return (
    <button
      onClick={onClick}
      title={excluded ? "Include in intent classification" : "Exclude from intent classification"}
      className={`p-1 rounded transition-colors ${excluded ? "text-red-500" : "text-zinc-300 hover:text-zinc-500"}`}
    >
      <Ban size={13} />
    </button>
  );
}

export function ExcludedFilterButton({ active, count, onClick, compact = false }) {
  if (compact) {
    return (
      <CompactFilterToggle active={active} count={count} onClick={onClick} label="Excluded" color="#EF4444">
        <Ban size={13} />
      </CompactFilterToggle>
    );
  }
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold rounded-lg transition-colors border"
      style={active
        ? { backgroundColor: "#EF4444", color: "#fff", borderColor: "#EF4444" }
        : { background: "#fff", color: "#71717a", borderColor: "#f4f4f5" }}
    >
      <Ban size={10} /> Excluded{count ? ` (${count})` : ""}
    </button>
  );
}

// useState that survives reloads — per-viewer UI choices (period, tab, filters).
// Storage can be blocked (private mode, cleared site data); fall back to `initial`.
export function usePersistentState(key, initial) {
  const storageKey = `sp:${key}`;
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw == null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch {}
  }, [storageKey, value]);
  return [value, setValue];
}
